import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, zl, carLabel, plDate, SERVICE_LABELS } from "@/lib/api";

const STATUS = { confirmed: ["Potwierdzona", "text-primary border-primary/50"], done: ["Zrealizowana", "text-emerald-400 border-emerald-400/40"], cancelled: ["Anulowana", "text-red-400 border-red-400/40"] };

const BookingRow = ({ b, onStatus, onDelete }) => (
  <div data-testid={`admin-booking-${b.id}`} className="panel p-5 grid md:grid-cols-[150px_1fr_1fr_auto] gap-4 items-start">
    <div>
      <div className="font-display text-lg font-semibold">{plDate(b.date)}</div>
      <div className="text-primary font-semibold">{b.time}</div>
      <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full border ${STATUS[b.status][1]}`}>{STATUS[b.status][0]}</span>
    </div>
    <div className="text-sm space-y-1">
      <div className="font-semibold">{b.name}</div>
      <div className="text-white/70">{b.phone} · {b.email}</div>
      {b.pickup && <div className="text-primary">Odbiór auta od klienta (dopłata)</div>}
      {b.address && <div className="text-white/70">Adres: {b.address}</div>}
      {b.invoice && <div className="text-white/70">Faktura · NIP: {b.nip || "—"}</div>}
      {b.notes && <div className="text-white/50 italic">„{b.notes}”</div>}
    </div>
    <div className="text-sm space-y-1">
      <div>{b.package_name} · {carLabel(b.car_type)}</div>
      <div className="text-white/60">{SERVICE_LABELS[b.service_type]}</div>
      {b.addons.length > 0 && <div className="text-white/60">+ {b.addons.map((a) => a.name).join(", ")}</div>}
      <div className="text-primary font-bold">{zl(b.total)}</div>
    </div>
    <div className="flex md:flex-col gap-2 text-xs">
      {b.status !== "done" && <button data-testid={`booking-done-${b.id}`} onClick={() => onStatus(b.id, "done")} className="px-3 py-1.5 rounded border border-white/15 hover:border-emerald-400">Zrealizowana</button>}
      {b.status !== "cancelled" && <button data-testid={`booking-cancel-${b.id}`} onClick={() => onStatus(b.id, "cancelled")} className="px-3 py-1.5 rounded border border-white/15 hover:border-red-400">Anuluj</button>}
      {b.status === "cancelled" && <button data-testid={`booking-restore-${b.id}`} onClick={() => onStatus(b.id, "confirmed")} className="px-3 py-1.5 rounded border border-white/15 hover:border-primary">Przywróć</button>}
      <button data-testid={`booking-delete-${b.id}`} onClick={() => onDelete(b.id)} className="px-3 py-1.5 rounded border border-white/15 text-red-400 hover:border-red-400">Usuń</button>
    </div>
  </div>
);

export const BookingsTab = () => {
  const [list, setList] = useState([]);
  const [all, setAll] = useState(false);
  const load = useCallback(() => api.get("/admin/bookings", { params: { upcoming: !all } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [all]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => { try { await api.patch(`/admin/bookings/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const onDelete = async (id) => { if (!window.confirm("Usunąć rezerwację?")) return; await api.delete(`/admin/bookings/${id}`); load(); };

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold">Rezerwacje <span className="text-white/40 text-lg">({list.length})</span></h2>
        <label className="flex items-center gap-3 text-sm">Pokaż archiwalne <Switch data-testid="bookings-show-all" checked={all} onCheckedChange={setAll} /></label>
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p data-testid="bookings-empty" className="text-muted-foreground">Brak rezerwacji.</p>}
        {list.map((b) => <BookingRow key={b.id} b={b} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
    </div>
  );
};
